import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Play, Pause, StopCircle, X, FileText, Activity, User, FileBox, Check } from "lucide-react";
import { useReactMediaRecorder } from "react-media-recorder";
import { ApiError } from "@/types/api";
import { NotesInterface } from "../components/medical/NotesInterface";
import { RecordingHistory } from "../components/medical/RecordingHistory";
import { WelcomeScreen } from "../components/welcome/WelcomeScreen";
import RecordingPreparationScreen from "../components/medical/RecordingPreparationScreen";
import { ImageAnalysisDetail } from "../components/medical/ImageAnalysisDetail";
import mockData from "../data/mockData.json";
import { NotesState, Recording } from "@/types/medical";
import axios from "../config/axios";
import { format } from "date-fns";
import { isRetryEligible } from "../utils/retryEligibility";

// Skeleton Loading Component for Notes Interface
const NotesSkeletonLoading = () => {
  return (
    <div className="flex-1 flex flex-col h-full overflow-auto p-8 bg-gradient-to-br from-[#FBFBFD] to-[#F7F9FF]">
      {/* Skeleton Header with enhanced design */}
      <div className="flex justify-between items-center mb-8">
        <div className="w-1/3">
          <div className="h-9 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer"></div>
          <div className="h-5 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded-lg w-2/3 mt-3 animate-shimmer" 
               style={{ animationDelay: '0.1s' }}></div>
        </div>
        <div className="flex space-x-3">
          <div className="h-10 w-28 bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 rounded-lg animate-shimmer shadow-sm"
               style={{ animationDelay: '0.2s' }}></div>
          <div className="h-10 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer shadow-sm"
               style={{ animationDelay: '0.3s' }}></div>
        </div>
      </div>
      
      {/* Skeleton Tabs with active indicator */}
      <div className="flex border-b border-gray-200 mb-8 relative">
        <div className="h-12 w-32 flex items-end px-3" style={{ animationDelay: '0.1s' }}>
          <div className="h-8 w-full bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 rounded-t-lg animate-shimmer"></div>
          {/* Active tab indicator */}
          <div className="absolute bottom-0 left-4 right-[calc(100%-124px)] h-0.5 bg-blue-500 rounded-full"></div>
        </div>
        <div className="h-12 w-32 flex items-end px-3" style={{ animationDelay: '0.15s' }}>
          <div className="h-7 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-t-lg animate-shimmer"></div>
        </div>
        <div className="h-12 w-32 flex items-end px-3" style={{ animationDelay: '0.2s' }}>
          <div className="h-7 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-t-lg animate-shimmer"></div>
        </div>
        <div className="h-12 w-32 flex items-end px-3" style={{ animationDelay: '0.25s' }}>
          <div className="h-7 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-t-lg animate-shimmer"></div>
        </div>
      </div>
      
      {/* Skeleton Content - SOAP Notes Layout with enhanced design */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        {/* First column - Subjective + Objective */}
        <div className="flex flex-col space-y-6">
          {/* Subjective */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 mr-3 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-500" />
              </div>
              <div className="h-6 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer"
                   style={{ animationDelay: '0.1s' }}></div>
            </div>
            <div className="space-y-3">
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-full" 
                   style={{ animationDelay: '0.2s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-5/6" 
                   style={{ animationDelay: '0.3s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-full" 
                   style={{ animationDelay: '0.4s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-4/6" 
                   style={{ animationDelay: '0.5s' }}></div>
            </div>
          </div>
          
          {/* Objective */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 mr-3 flex items-center justify-center">
                <Activity className="w-5 h-5 text-green-500" />
              </div>
              <div className="h-6 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer"
                   style={{ animationDelay: '0.15s' }}></div>
            </div>
            <div className="space-y-3">
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-full" 
                   style={{ animationDelay: '0.25s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-5/6" 
                   style={{ animationDelay: '0.35s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-full" 
                   style={{ animationDelay: '0.45s' }}></div>
            </div>
          </div>
        </div>
        
        {/* Second column - Assessment + Plan */}
        <div className="flex flex-col space-y-6">
          {/* Assessment */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 mr-3 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-500" />
              </div>
              <div className="h-6 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer"
                   style={{ animationDelay: '0.2s' }}></div>
            </div>
            <div className="space-y-3">
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-full" 
                   style={{ animationDelay: '0.3s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-3/4" 
                   style={{ animationDelay: '0.4s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-5/6" 
                   style={{ animationDelay: '0.5s' }}></div>
            </div>
          </div>
          
          {/* Plan */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 mr-3 flex items-center justify-center">
                <FileBox className="w-5 h-5 text-orange-500" />
              </div>
              <div className="h-6 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer"
                   style={{ animationDelay: '0.25s' }}></div>
            </div>
            <div className="space-y-3">
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-full" 
                   style={{ animationDelay: '0.35s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-2/3" 
                   style={{ animationDelay: '0.45s' }}></div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-5/6" 
                   style={{ animationDelay: '0.55s' }}></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Skeleton Codes Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-8">
        <div className="flex justify-between items-center mb-5">
          <div className="h-6 w-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer"></div>
          <div className="h-8 w-24 bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 rounded-lg animate-shimmer shadow-sm"
               style={{ animationDelay: '0.2s' }}></div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg border border-gray-100 p-3 flex items-center" 
                 style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="h-8 w-16 bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 rounded-lg animate-shimmer mr-3"
                   style={{ animationDelay: `${i * 0.1 + 0.1}s` }}></div>
              <div className="flex-1">
                <div className="h-3 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-full mb-2"
                     style={{ animationDelay: `${i * 0.1 + 0.2}s` }}></div>
                <div className="h-3 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded animate-shimmer w-2/3"
                     style={{ animationDelay: `${i * 0.1 + 0.3}s` }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Loading indicator at bottom */}
      <div className="mt-auto pt-8 flex justify-center items-center text-blue-600">
        <div className="relative w-6 h-6 mr-3">
          <div className="absolute inset-0 rounded-full border-2 border-current border-t-transparent animate-spin"></div>
          <div className="absolute inset-0 rounded-full border border-current opacity-20"></div>
        </div>
        <span className="text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          Loading medical details...
        </span>
      </div>
    </div>
  );
};

// Update Safari detection to be more accurate
const isSafari =
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Mac") &&
    navigator.userAgent.includes("Safari"));

// Add Chrome detection
const isChrome = /chrome/i.test(navigator.userAgent) && !isSafari;

export default function MyScribes() {
  const [isSending, setIsSending] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [message, setMessage] = useState<{
    type: "error" | "success";
    text: string;
  } | null>(null);
  const [recordingCount, setRecordingCount] = useState(-1);

  const [selectedRecordingId, setSelectedRecordingId] = useState<string | null>(
    null
  );
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [recordingTitle, setRecordingTitle] = useState("Patient Visit Notes");
  const [searchQuery, setSearchQuery] = useState("");
  const [duration, setDuration] = useState(0);
  const [selectedRecordingData, setSelectedRecordingData] = useState<any>(null);
  const [notes, setNotes] = useState<NotesState>({
    transcript: "",
    icd_codes: [],
    cpt_codes: [],
    processed_content: {},
    patient_instructions: {
      email: {
        subject: "",
        body: "",
      },
    },
  });
  const [emailContent, setEmailContent] = useState(
    notes.patient_instructions?.email?.body || ""
  );
  const [recordingDate] = useState(new Date());
  // Last time (epoch ms) a progress/WebSocket update was seen for the selected recording -
  // used to detect "Processing"/"transcribing" stuck with no progress for 30+ min.
  const lastActivityRef = useRef<Map<string, number>>(new Map());
  // Recordings that were successfully retried - suppress the button until they
  // re-enter an eligible state (a fresh status update clears the entry).
  const [retriedRecordingIds, setRetriedRecordingIds] = useState<Set<string>>(new Set());

  // Function to check if recording is completed or if we should show the preparation screen
  const isRecordingCompleted = () => {
    if (!notes.processing_status) return true; // Default to showing notes interface if no status

    const status = notes.processing_status.status.toLowerCase();
    return status === "completed";
  };

  // Function to check if the selected recording is an image analysis recording
  const isImageAnalysisRecording = () => {
    return selectedRecordingData &&
           selectedRecordingData.media_type === "image" &&
           selectedRecordingData.image_analysis;
  };

  // Handle status updates from recording history (centralized status management)
  const handleRecordingStatusUpdate = useCallback((recordingId: string, status: string, progress?: number, details?: any) => {
    lastActivityRef.current.set(recordingId, Date.now());
    setRetriedRecordingIds(prev => {
      if (!prev.has(recordingId)) return prev;
      const next = new Set(prev);
      next.delete(recordingId);
      return next;
    });

    if (recordingId === selectedRecordingId) {
      console.log(`📋 MyScribes: Updating recording ${recordingId} - status: ${status}, progress: ${progress}%`);

      setNotes(prev => {
        const currentProgress = prev.processing_status?.details?.progress || 0;
        
        // Skip update if new progress is lower than current progress (ignore stale/out-of-order events)
        if (progress !== undefined && progress < currentProgress && status.toLowerCase() !== "failed" && status.toLowerCase() !== "completed") {
          console.log(`Ignoring stale progress update for selected recording ${recordingId} - current: ${currentProgress}%, new: ${progress}%`);
          return prev;
        }
        
        return {
          ...prev,
          processing_status: {
            ...prev.processing_status,
            status,
            details: {
              ...prev.processing_status?.details,
              progress: progress !== undefined ? progress : prev.processing_status?.details?.progress,
              message: details?.message || `${status}: ${progress || 0}%`,
              is_processing: !['completed', 'analyzed'].includes(status.toLowerCase()) && status.toLowerCase() !== "failed",
              is_processed: ['completed', 'analyzed'].includes(status.toLowerCase()),
              has_error: status.toLowerCase() === "failed",
            }
          }
        };
      });
    }
  }, [selectedRecordingId]);

  // Polling fallback: re-fetch selected recording status every 30s if not in terminal state
  useEffect(() => {
    const TERMINAL_STATUSES = ["completed", "analyzed", "failed"];
    const POLL_INTERVAL = 30000; // 30 seconds

    if (!selectedRecordingId || selectedRecordingId === "current") return;

    const currentStatus = (notes.processing_status?.status || "").toLowerCase();
    if (!currentStatus || TERMINAL_STATUSES.includes(currentStatus)) return;

    console.log(`Starting 30s polling for selected recording ${selectedRecordingId} (status: ${currentStatus})`);

    const intervalId = setInterval(async () => {
      try {
        const url = `/api/scribing/speech/recordings/${selectedRecordingId}`;
        const response = await axios.get(url);
        if (response.data) {
          const data = response.data;
          const newStatus = (data.processing_status?.status || data.status || "").toLowerCase();
          console.log(`Polling update for recording ${selectedRecordingId}: status=${newStatus}`);

          // Polling only acts on terminal states - WebSocket is source of truth for granular statuses
          // REST API returns generic "processing" until completion, so intermediate updates would overwrite granular WebSocket statuses
          setNotes(prev => {
            if (!TERMINAL_STATUSES.includes(newStatus)) {
              console.log(`Polling: status is non-terminal (${newStatus}), skipping - WebSocket handles granular updates`);
              return prev;
            }

            const prevStatus = (prev.processing_status?.status || "").toLowerCase();
            if (prevStatus === newStatus) {
              console.log(`Polling: status unchanged (${newStatus}), skipping update`);
              return prev;
            }

            console.log(`Polling: terminal status detected ${prevStatus} -> ${newStatus}, updating`);

            // Recording is done - update notes with full data
            setSelectedRecordingData(data);
            setIsSending(false);
            const notesSource = data.notes || data;
            return {
              transcript: notesSource.transcript || "",
              icd_codes: notesSource.icd_codes || [],
              cpt_codes: notesSource.cpt_codes || [],
              processed_content: notesSource.processed_content || {},
              patient_instructions: notesSource.patient_instructions || { email: { subject: "", body: "" } },
              processing_status: data.processing_status,
              processing_options: data.processing_options || {},
              template: data.template || {},
            };
          });
        }
      } catch (err) {
        console.error("Polling for selected recording failed:", err);
      }
    }, POLL_INTERVAL);

    return () => {
      console.log(`Stopping polling for recording ${selectedRecordingId}`);
      clearInterval(intervalId);
    };
  }, [selectedRecordingId, notes.processing_status?.status]);

  // Function to handle refresh for failed recordings
  const handleRefreshRecording = async () => {
    console.log("Refreshing page to reload recording data");
    window.location.reload();
  };

  // Optimistically flip the detail view into the normal "Processing" UI after a
  // successful retry - resumes the same WebSocket/polling flow a fresh recording uses.
  const handleRetryStarted = useCallback((recordingId: string) => {
    lastActivityRef.current.set(recordingId, Date.now());
    setRetriedRecordingIds(prev => new Set(prev).add(recordingId));

    if (recordingId === selectedRecordingId) {
      setNotes(prev => ({
        ...prev,
        processing_status: {
          status: "Processing",
          details: {
            ...prev.processing_status?.details,
            is_processing: true,
            is_uploaded: true,
            is_processed: false,
            has_error: false,
            progress: 5,
            message: "Retrying processing...",
          },
        },
      }));
    }
  }, [selectedRecordingId]);

  const handleRetryError = useCallback((_recordingId: string, message: string) => {
    setMessage({ type: "error", text: message });
    setTimeout(() => setMessage(null), 4000);
  }, []);

  const handleRetryToast = useCallback((toast: { type: "error" | "success"; text: string }) => {
    setMessage(toast);
    setTimeout(() => setMessage(null), 4000);
  }, []);

  const handleChange = useCallback(
    (section: keyof NotesState | string, value: string) => {
      // Handle special action for refreshing a recording
      if (section === "refresh_recording") {
        console.log("Refresh recording requested for:", value);
        handleRefreshRecording();
        return;
      }
      
      // Regular state updates for current recording
      if (selectedRecordingId === "current") {
        setNotes((prev) => ({
          ...prev,
          [section as keyof NotesState]: value,
        }));
      }
    },
    [selectedRecordingId, handleRefreshRecording]
  );

  const handleSendEmail = async (): Promise<void> => {
    return new Promise<void>((resolve) => setTimeout(resolve, 2000));
  };

  const handleRecordingSelect = useCallback(
    async (recordingId: string, status: string, details: any) => {
      console.log("handleRecordingSelect called with:", {
        recordingId,
        status,
        details,
      });
      setSelectedRecordingId(recordingId);

      console.log("details", details);

      // Check if we're in a loading state first (before showing any errors)
      if (status.toLowerCase() === "loading") {
        console.log("Recording is in loading state, showing skeleton UI");
        setNotes(prevNotes => ({
          ...prevNotes,
          processing_status: {
            status: "loading",
            details: {
              is_processing: true,
              is_uploaded: true,
              is_processed: false,
              has_error: false,
              is_loading: true,
              isSkeletonLoading: true,
              progress: 0,
              message: details.message || "Loading data...",
            }
          }
        }));
        
        // We're waiting for data to load, don't set isSending state
        return;
      }

      // If it's an existing recording
      setIsSending(true);
      try {
        console.log("MyScribes - Recording details:", details);

        // Store the full recording data for image analysis detection
        setSelectedRecordingData(details);

        // Use status and progress from details (from WebSocket or API)
        const statusMessage = details?.message || `Recording is ${status}`;
        const progressValue = details?.progress || 0;
        
        // Handle both API response structures: notes nested under `notes` key or at top level
        const notesSource = details.notes || details;
        const hasNotes = notesSource.transcript || notesSource.processed_content || notesSource.icd_codes;

        if (hasNotes) {
          const notesData = {
            transcript: notesSource.transcript || "",
            processed_content: notesSource.processed_content || {},
            icd_codes: notesSource.icd_codes || [],
            cpt_codes: notesSource.cpt_codes || [],
            patient_instructions: notesSource.patient_instructions || {
              email: { subject: "", body: "" },
            },
            processing_options: details.processing_options || {},
            template: details.template || {},
            // Use processing status from details or create simple one
            processing_status: details.processing_status || {
              status: status,
              details: {
                is_processing: !['completed', 'analyzed'].includes(status.toLowerCase()) && status.toLowerCase() !== "failed",
                is_processed: ['completed', 'analyzed'].includes(status.toLowerCase()),
                has_error: status.toLowerCase() === "failed",
                is_uploaded: true,
                progress: progressValue,
                message: statusMessage,
              }
            }
          };

          console.log("MyScribes - Setting notes with:", notesData);
          setNotes(notesData);

          if (notesSource.patient_instructions?.email?.body) {
            setEmailContent(notesSource.patient_instructions.email.body);
          }
        } else {
          console.error("No notes data in the response");
          // Reset notes data instead of keeping previous notes when details.notes is null
          setNotes({
            transcript: "",
            icd_codes: [],
            cpt_codes: [],
            processed_content: {},
            patient_instructions: {
              email: { subject: "", body: "" },
            },
            processing_status: {
              status: status,
              details: {
                is_processing: !['completed', 'analyzed'].includes(status.toLowerCase()) && status.toLowerCase() !== "failed",
                is_processed: ['completed', 'analyzed'].includes(status.toLowerCase()),
                has_error: status.toLowerCase() === "failed",
                is_uploaded: true,
                progress: progressValue,
                message: statusMessage,
              }
            }
          });

          // Also reset email content
          setEmailContent("");
        }

        if (details.title) {
          setRecordingTitle(details.title);
          setDuration(details.duration);
        }
      } catch (error) {
        console.error("Error processing recording data:", error);
        setMessage({
          type: "error",
          text: "Failed to load recording details",
        });
        
        // Set error status even when there's an exception
        setNotes(prevNotes => ({
          ...prevNotes,
          processing_status: {
            status: "failed",
            details: {
              is_processing: false,
              is_processed: false,
              has_error: true,
              is_uploaded: true,
              progress: 0,
              message: "Error loading recording details",
            }
          }
        }));
      } finally {
        setIsSending(false);
      }
    },
    []
  );

  const handleVersionSelect = useCallback(
    async (versionRecordingId: string, versionId: string, isDefault: boolean) => {
      console.log("Version selected:", { versionRecordingId, versionId, isDefault });
      
      // Update the selected transaction ID (using versionId)
      setSelectedTransactionId(versionId);
      
      // Use the recording_id from the selected version directly
      setIsSending(true);
      
      try {
        // Use the recording_id from the selected version, no transaction_id parameter needed
        const url = `/api/scribing/speech/recordings/${versionRecordingId}`;
        
        const response = await axios.get(url);
        const recordingData = response.data;
        console.log("Version API Response:", recordingData);
          
          // Update notes with the new version data
          if (recordingData.notes) {
            const notesData = {
              transcript: recordingData.notes.transcript || "",
              processed_content: recordingData.notes.processed_content || {},
              icd_codes: recordingData.notes.icd_codes || [],
              cpt_codes: recordingData.notes.cpt_codes || [],
              patient_instructions: recordingData.notes.patient_instructions || {
                email: { subject: "", body: "" },
              },
              processing_options: recordingData.processing_options || {},
              template: recordingData.template || {},
              processing_status: {
                status: "completed",
                details: {
                  is_processing: false,
                  is_processed: true,
                  has_error: false,
                  is_uploaded: true,
                  progress: 100,
                  message: "Processing complete!",
                }
              }
            };

            setNotes(notesData);

            if (recordingData.notes.patient_instructions?.email?.body) {
              setEmailContent(recordingData.notes.patient_instructions.email.body);
            }
          }

          if (recordingData.title) {
            setRecordingTitle(recordingData.title);
            setDuration(recordingData.duration || 0);
          }
          
        } catch (error) {
          console.error("Error fetching recording version:", error);
          setMessage({
            type: "error",
            text: "Failed to load recording version",
          });
        } finally {
          setIsSending(false);
        }
    },
    []
  );

  const handleRefreshCurrentRecording = useCallback(
    async () => {
      if (selectedRecordingId && selectedRecordingId !== "current") {
        setIsSending(true);
        
        try {
          // Fetch the current default version of the recording
          const response = await axios.get(`/api/scribing/speech/recordings/${selectedRecordingId}`);
          const recordingData = response.data;
          
          // Update notes with the refreshed data
          if (recordingData.notes) {
            const notesData = {
              transcript: recordingData.notes.transcript || "",
              processed_content: recordingData.notes.processed_content || {},
              icd_codes: recordingData.notes.icd_codes || [],
              cpt_codes: recordingData.notes.cpt_codes || [],
              patient_instructions: recordingData.notes.patient_instructions || {
                email: { subject: "", body: "" },
              },
              processing_options: recordingData.processing_options || {},
              template: recordingData.template || {},
              processing_status: {
                status: "completed",
                details: {
                  is_processing: false,
                  is_processed: true,
                  has_error: false,
                  is_uploaded: true,
                  progress: 100,
                  message: "Processing complete!",
                }
              }
            };

            setNotes(notesData);

            if (recordingData.notes.patient_instructions?.email?.body) {
              setEmailContent(recordingData.notes.patient_instructions.email.body);
            }
          }

          if (recordingData.title) {
            setRecordingTitle(recordingData.title);
            setDuration(recordingData.duration || 0);
          }
          
          // Clear the selected transaction ID since we're now showing the default
          setSelectedTransactionId(null);
          
        } catch (error) {
          console.error("Error refreshing recording:", error);
          setMessage({
            type: "error",
            text: "Failed to refresh recording",
          });
        } finally {
          setIsSending(false);
        }
      }
    },
    [selectedRecordingId]
  );

  const handleDeleteRecording = useCallback(async () => {
    if (selectedRecordingId) {
      try {
        // Reset states
        setSelectedRecordingId(null);
        setSelectedRecordingData(null);
        setNotes({
          transcript: "",
          icd_codes: [],
          cpt_codes: [],
          processed_content: {},
          patient_instructions: {
            email: {
              subject: "",
              body: "",
            },
          },
        });
        setEmailContent("");
        setRecordingTitle("Patient Visit Notes");

        // Show success message
        setMessage({
          type: "success",
          text: "Recording deleted successfully",
        });

        // Clear message after 3 seconds
        setTimeout(() => {
          setMessage(null);
        }, 3000);

        // Refresh the page to update the recordings list
        window.location.reload();
      } catch (error) {
        console.error("Error handling delete:", error);
        setMessage({
          type: "error",
          text: "Failed to delete recording",
        });
      }
    }
  }, [selectedRecordingId]);


  return (
    <div className="h-full bg-[#FBFBFD]">
      <div className="h-full flex overflow-hidden">
        {recordingCount === 0 && !transcription && !isSending ? (
          <WelcomeScreen />
        ) : (
          <>
            <RecordingHistory
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedRecordingId={selectedRecordingId}
              onRecordingSelect={handleRecordingSelect}
              transcription={transcription}
              isSending={isSending}
              recordingTitle={recordingTitle}
              recordingDate={recordingDate}
              onRecordingsCountChange={setRecordingCount}
              notes={notes}
              onRecordingStatusUpdate={handleRecordingStatusUpdate}
              onRetryToast={handleRetryToast}
            />

            <div className="flex-1 flex flex-col">
              <AnimatePresence mode="wait">
                {selectedRecordingId && (
                  <>
                    {/* Show skeleton loading when a recording is loading */}
                    {notes.processing_status?.details?.isSkeletonLoading ? (
                      <NotesSkeletonLoading />
                    ) : !isRecordingCompleted() ? (
                      <RecordingPreparationScreen
                        status={notes.processing_status?.status || "analyzing"}
                        details={{
                          message: notes.processing_status?.details?.message || "Processing your recording...",
                          progress: notes.processing_status?.details?.progress || 0,
                        }}
                        title={recordingTitle}
                        date={format(recordingDate, "MMMM d, yyyy h:mm a")}
                        patientName={notes.processed_content?.processing_options?.patient_name}
                        onRefresh={handleRefreshRecording}
                        showRetry={
                          !!selectedRecordingId &&
                          !retriedRecordingIds.has(selectedRecordingId) &&
                          isRetryEligible({
                            status: notes.processing_status?.status,
                            recordingDate: selectedRecordingData?.date || selectedRecordingData?.created_at,
                            lastActivityAt: lastActivityRef.current.get(selectedRecordingId),
                            notes: selectedRecordingData?.notes,
                          })
                        }
                        recordingId={selectedRecordingId || undefined}
                        templateId={selectedRecordingData?.template?.id}
                        onRetryStarted={handleRetryStarted}
                        onRetryError={handleRetryError}
                      />
                    ) : isImageAnalysisRecording() ? (
                      <ImageAnalysisDetail
                        analysisId={selectedRecordingId}
                      />
                    ) : (
                      <NotesInterface
                        notes={notes}
                        onNotesChange={handleChange}
                        onSendEmail={handleSendEmail}
                        onError={(error) =>
                          setMessage({ type: "error", text: error })
                        }
                        onDelete={handleDeleteRecording}
                        onDeleteClick={handleDeleteRecording}
                        isLoading={isSending}
                        title={recordingTitle}
                        onTitleChange={setRecordingTitle}
                        emailContent={emailContent}
                        selectedRecordingId={selectedRecordingId}
                        selectedTemplateId={undefined}
                        readOnly={false}
                        duration={duration}
                        onVersionSelect={handleVersionSelect}
                        onRefreshRecording={handleRefreshCurrentRecording}
                        selectedTransactionId={selectedTransactionId}
                      />
                    )}
                  </>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>

      {/* Error/Success Message */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-4 right-4 p-4 rounded-lg flex items-center shadow-lg ${
              message.type === "error"
                ? "bg-red-100 text-red-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {message.type === "error" ? (
              <X className="w-5 h-5 mr-2" />
            ) : (
              <Check className="w-5 h-5 mr-2" />
            )}
            <p className="text-sm font-medium">{message.text}</p>
            <button
              onClick={() => setMessage(null)}
              className="ml-4 p-1 rounded-full hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
